import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Zap, Phone, Mail, MapPin, CheckCircle, ChevronDown, ChevronUp,
  Star, ClipboardCheck, ArrowRight, Shield, Users, Home, Building,
  AlertTriangle, Lightbulb, Wifi, Thermometer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { locations } from './LocationPage';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TESLA_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/vcyr36jn_tesla-certified-installer.jpg";
const GENERAC_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/40wcnfw6_Generac-Installer.png";

// Service images (same as ServicesPage)
const SERVICE_IMAGES = {
  'ev-charger-installation': 'https://images.unsplash.com/photo-1765272088009-100c96a4cd4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxlbGVjdHJpYyUyMHZlaGljbGUlMjBjaGFyZ2luZyUyMHN0YXRpb24lMjBob21lfGVufDB8fHx8MTc3MTU3NzI3NXww&ixlib=rb-4.1.0&q=85',
  'panel-replacement': 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg',
  'generator-installation': 'https://images.pexels.com/photos/5767605/pexels-photo-5767605.jpeg',
  'bc-hydro-power-smart': 'https://images.unsplash.com/photo-1764885517995-a4a4f940e1a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVsJTIwaW5zdGFsbGF0aW9uJTIwY29tbWVyY2lhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4MjYxMTYzNnww&ixlib=rb-4.1.0&q=85',
  'lighting-installation': 'https://images.pexels.com/photos/31737859/pexels-photo-31737859.jpeg',
  'electrical-planning-reports': 'https://images.pexels.com/photos/7359566/pexels-photo-7359566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'residential-electrical': 'https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg',
  'commercial-electrical': 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
};

const ServiceLocationPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', city: '', details: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/service-location/${slug}`);
        setData(res.data);
        // Find location data
        const loc = locations[res.data.location_slug];
        setLocation(loc || { name: res.data.location_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '), region: 'Vancouver Island' });
      } catch (e) {
        console.error('Failed to load service-location page:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
  }, [slug]);

  // SEO meta tags
  useEffect(() => {
    if (!data || !location) return;
    document.title = `${data.service_title} ${location.name} BC | Island Coastal Electric`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.setAttribute('name', 'description'); document.head.appendChild(meta); }
    meta.setAttribute('content', `Professional ${data.service_title.toLowerCase()} in ${location.name}, BC. ${data.service_description} Licensed, insured. Free estimates — call 778.960.2377.`);

    // FAQ schema
    if (data.faqs?.length > 0) {
      let script = document.getElementById('faq-schema-loc');
      if (!script) { script = document.createElement('script'); script.id = 'faq-schema-loc'; script.type = 'application/ld+json'; document.head.appendChild(script); }
      script.textContent = JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": data.faqs.map(f => ({ "@type": "Question", "name": f.question, "acceptedAnswer": { "@type": "Answer", "text": f.answer } }))
      });
    }
    // LocalBusiness + Service schema
    let svcScript = document.getElementById('service-schema-loc');
    if (!svcScript) { svcScript = document.createElement('script'); svcScript.id = 'service-schema-loc'; svcScript.type = 'application/ld+json'; document.head.appendChild(svcScript); }
    svcScript.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Service", "name": `${data.service_title} in ${location.name}`,
      "description": data.service_description,
      "provider": { "@type": "LocalBusiness", "name": "Island Coastal Electric", "telephone": "778-960-2377",
        "address": { "@type": "PostalAddress", "streetAddress": "2668 Sooke Road", "addressLocality": "Victoria", "addressRegion": "BC", "postalCode": "V9B 1Y6", "addressCountry": "CA" }
      },
      "areaServed": { "@type": "City", "name": location.name }
    });

    return () => { document.getElementById('faq-schema-loc')?.remove(); document.getElementById('service-schema-loc')?.remove(); document.title = 'Island Coastal Electric'; };
  }, [data, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) { toast.error('Name and phone are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/leads`, {
        name: formData.name, phone: formData.phone,
        city: formData.city || location?.name || '',
        details: formData.details,
        service: data?.service_slug || '',
        source_page: slug || '',
      });
      toast.success('Request sent! We\'ll call you back shortly.');
      setFormData({ name: '', phone: '', city: '', details: '' });
    } catch (err) {
      toast.error('Failed to send. Please call us at 778.960.2377.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-primary-dark pt-28 flex items-center justify-center"><div className="animate-pulse text-white">Loading...</div></div>;
  if (!data) return <div className="min-h-screen bg-primary-dark pt-28 flex items-center justify-center"><div className="text-center"><h1 className="text-2xl font-bold text-white mb-4">Page Not Found</h1><Link to="/services" className="text-electric-teal">Back to Services</Link></div></div>;

  const isEPR = data.service_slug === 'electrical-planning-reports';

  return (
    <div className="min-h-screen bg-primary-dark pt-20" data-testid="service-location-page">
      {/* Hero — Phone, Email, Booking Front & Center */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0">
          <img src={SERVICE_IMAGES[data.service_slug] || ''} alt={data.service_title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary-dark/95 to-primary-dark/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Left — Title & CTAs */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 text-electric-teal text-sm font-bold uppercase tracking-widest mb-4">
                <MapPin className="w-4 h-4" /> {location?.name}, BC
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-none mb-4" data-testid="hero-title">
                {data.service_title}<br />
                <span className="text-electric-teal">in {location?.name}</span>
              </h1>
              <p className="text-white/60 text-lg mb-8 max-w-xl">{data.service_description}</p>
              
              {/* Contact CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <a href="tel:7789602377" className="inline-flex items-center gap-2 bg-electric-teal text-primary-dark font-bold px-6 py-4 rounded-lg text-lg hover:bg-white transition-all" data-testid="hero-phone-btn">
                  <Phone className="w-5 h-5" /> 778.960.2377
                </a>
                <a href="mailto:info@islandcoastalelectric.ca" className="inline-flex items-center gap-2 bg-white/10 text-white font-bold px-6 py-4 rounded-lg border border-white/20 hover:bg-white/20 transition-all" data-testid="hero-email-btn">
                  <Mail className="w-5 h-5" /> Email Us
                </a>
              </div>
              <div className="flex items-center gap-4 text-white/40 text-sm flex-wrap">
                <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-electric-teal" /> BC Hydro Alliance Member</span>
                <span className="flex items-center gap-1"><Star className="w-4 h-4 text-electric-teal" /> 5-Star Rated</span>
                {data.service_slug === 'ev-charger-installation' && (
                  <img src={TESLA_BADGE} alt="Tesla Certified" className="h-8 w-auto rounded bg-white p-0.5" data-testid="hero-tesla-badge" />
                )}
                {data.service_slug === 'generator-installation' && (
                  <img src={GENERAC_BADGE} alt="Generac Certified" className="h-8 w-auto rounded bg-white p-0.5" data-testid="hero-generac-badge" />
                )}
              </div>
            </div>

            {/* Right — Quick Quote Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-secondary-dark/95 backdrop-blur-sm rounded-xl border border-white/10 p-6" data-testid="quote-form">
                <h3 className="text-white font-bold text-lg mb-1">Get a Free Quote</h3>
                <p className="text-white/40 text-xs mb-4">We&apos;ll call you back within 2 hours</p>
                <div className="space-y-3">
                  <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Your Name *" className="bg-primary-dark border-white/20 text-white" data-testid="form-name" required />
                  <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="Phone Number *" className="bg-primary-dark border-white/20 text-white" data-testid="form-phone" required />
                  <Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} placeholder={`City (e.g., ${location?.name})`} className="bg-primary-dark border-white/20 text-white" data-testid="form-city" />
                  <Input value={formData.details} onChange={e => setFormData({...formData, details: e.target.value})} placeholder={isEPR ? "Building type / # of units?" : "Vehicle / charger / project details?"} className="bg-primary-dark border-white/20 text-white" data-testid="form-details" />
                  <Button type="submit" disabled={submitting} className="w-full bg-electric-teal text-primary-dark font-bold py-4 text-base" data-testid="form-submit">
                    <Zap className="w-4 h-4 mr-2" /> {submitting ? 'Sending...' : 'Get Free Estimate'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Badge Callout */}
      {(data.service_slug === 'ev-charger-installation' || data.service_slug === 'generator-installation') && (
        <section className="py-8 bg-primary-dark border-y border-white/5" data-testid="certification-callout">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-center sm:text-left">
              {data.service_slug === 'ev-charger-installation' && (
                <>
                  <img src={TESLA_BADGE} alt="Tesla Certified Installer" className="h-20 w-auto rounded-lg bg-white p-1" data-testid="cert-tesla-badge" />
                  <div>
                    <p className="text-white font-bold text-lg">Tesla Certified Installer</p>
                    <p className="text-electric-teal text-sm font-semibold">Factory-Trained for Wall Connector Installations</p>
                    <p className="text-white/40 text-xs mt-1">Trusted by Tesla for professional EV charger installations across Vancouver Island</p>
                  </div>
                </>
              )}
              {data.service_slug === 'generator-installation' && (
                <>
                  <img src={GENERAC_BADGE} alt="Generac Certified Installer" className="h-20 w-auto rounded bg-white p-1" data-testid="cert-generac-badge" />
                  <div>
                    <p className="text-white font-bold text-lg">Generac Certified Installer</p>
                    <p className="text-electric-teal text-sm font-semibold">Factory-Trained for Home &amp; Commercial Generators</p>
                    <p className="text-white/40 text-xs mt-1">Authorized to sell, install, and service Generac whole-home generators across Vancouver Island</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Checklist Section */}
      {data.checklist?.length > 0 && (
        <section className="py-16 bg-secondary-dark" data-testid="checklist-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm"><ClipboardCheck className="w-4 h-4 inline mr-1" /> {isEPR ? 'What We Check' : 'Our Checklist'}</span>
              <h2 className="text-3xl font-bold text-white mt-3">{isEPR ? 'Complete EPR Assessment Checklist' : `What Goes Into Your ${data.service_title}`}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.checklist.map((item, i) => (
                <motion.div key={`cl-${item.item.slice(0, 15)}-${i}`} initial={{opacity:0,y:15}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.05}}
                  className="bg-primary-dark rounded-lg p-5 border border-white/5 hover:border-electric-teal/20 transition-colors">
                  <CheckCircle className="w-5 h-5 text-electric-teal mb-2" />
                  <p className="text-white font-semibold text-sm">{item.item}</p>
                  <p className="text-white/40 text-xs mt-1">{item.detail}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* City-Specific Content — Hazards & Smart Solutions */}
      {data.city_theme && (
        <section className="py-16 bg-primary-dark" data-testid="city-theme-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* City Description */}
            <div className="max-w-4xl mb-12">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
                <MapPin className="w-4 h-4 inline mr-1" /> Local Expertise — {location?.name}
              </span>
              <h2 className="text-3xl font-bold text-white mt-3 mb-4">{data.city_theme.tagline}</h2>
              <p className="text-white/60 text-base leading-relaxed">{data.city_theme.description}</p>
            </div>

            {/* Hazards Grid */}
            {data.city_theme.hazards?.length > 0 && (
              <div className="mb-12">
                <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" /> 
                  {data.city_theme.theme === 'storm-resilience' ? 'Risks Without Backup Power' :
                   data.city_theme.theme === 'vacation-property' ? 'Risks for Unattended Properties' :
                   data.city_theme.theme === 'new-construction-quality' ? 'Builder-Grade Shortcuts to Watch For' :
                   data.city_theme.theme === 'remote-resilience' ? 'Northern Island Electrical Challenges' :
                   'Common Hazards in Older Homes'}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.city_theme.hazards.map((h, i) => (
                    <motion.div key={`hz-${h.name.slice(0, 15)}-${i}`} initial={{opacity:0,y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.06}}
                      className="bg-secondary-dark rounded-lg p-5 border border-amber-500/10 hover:border-amber-500/20 transition-colors">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mb-2" />
                      <p className="text-white font-semibold text-sm mb-1">{h.name}</p>
                      <p className="text-white/40 text-xs leading-relaxed">{h.detail}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Smart Solutions (Cowichan/Duncan specific) */}
            {data.city_theme.smart_solutions?.length > 0 && (
              <div>
                <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-electric-teal" /> Smart Monitoring Solutions
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.city_theme.smart_solutions.map((s, i) => (
                    <motion.div key={`ss-${s.name.slice(0, 15)}-${i}`} initial={{opacity:0,y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.06}}
                      className="bg-secondary-dark rounded-lg p-5 border border-electric-teal/10 hover:border-electric-teal/20 transition-colors">
                      <Lightbulb className="w-4 h-4 text-electric-teal mb-2" />
                      <p className="text-white font-semibold text-sm mb-1">{s.name}</p>
                      <p className="text-white/40 text-xs leading-relaxed">{s.detail}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* EPR-Specific: Landlords & Home Buyers Sections */}
      {isEPR && (
        <section className="py-16 bg-primary-dark" data-testid="epr-audiences">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-secondary-dark rounded-xl p-8 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Building className="w-6 h-6 text-electric-teal" />
                  <h3 className="text-white font-bold text-xl">For Landlords & Property Managers</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  If you manage rental properties or strata buildings with 5+ units, an EPR is now legally required in BC. 
                  The report becomes part of your Form B disclosure package — meaning every potential buyer will see it.
                </p>
                <ul className="space-y-2">
                  {['Meet your legal compliance deadline', 'Protect yourself during property transactions', 'Plan future electrical upgrades with cost estimates', 'Understand EV charging infrastructure needs'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/50 text-sm"><CheckCircle className="w-4 h-4 text-electric-teal flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-secondary-dark rounded-xl p-8 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Home className="w-6 h-6 text-electric-teal" />
                  <h3 className="text-white font-bold text-xl">For Home Buyers & Sellers</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  Buying or selling a strata unit? The EPR is now part of the Form B disclosure. As a buyer, it tells you 
                  the building&apos;s electrical health. As a seller, having a current EPR removes a potential deal blocker.
                </p>
                <ul className="space-y-2">
                  {['Know the building\'s electrical capacity before buying', 'Understand future upgrade costs (EV, heat pumps)', 'Avoid surprises during the sales process', 'Required for Form B disclosure on all sales'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/50 text-sm"><CheckCircle className="w-4 h-4 text-electric-teal flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Process Steps */}
      {data.process_steps?.length > 0 && (
        <section className="py-16 bg-primary-dark" data-testid="process-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">How It Works</span>
              <h2 className="text-3xl font-bold text-white mt-3">{isEPR ? 'The EPR Process' : `${data.service_title} in ${location?.name}`}</h2>
            </div>
            <div className="grid md:grid-cols-5 gap-3">
              {data.process_steps.sort((a,b) => a.step - b.step).map((step, i) => (
                <motion.div key={i} initial={{opacity:0,y:15}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.08}}
                  className="bg-secondary-dark rounded-lg p-5 border border-white/5">
                  <span className="w-7 h-7 rounded-full bg-electric-teal/10 border border-electric-teal/30 flex items-center justify-center text-electric-teal font-bold text-xs mb-3">{step.step}</span>
                  <h4 className="text-white font-bold text-sm mb-1">{step.title}</h4>
                  <p className="text-white/40 text-xs leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {data.testimonials?.length > 0 && (
        <section className="py-16 bg-secondary-dark" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">What Our Customers Say</span>
              <h2 className="text-3xl font-bold text-white mt-3">Real Reviews from Real Jobs</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {data.testimonials.map((t, i) => (
                <motion.div key={i} initial={{opacity:0,y:15}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
                  className="bg-primary-dark rounded-lg p-6 border border-white/5">
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(t.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-electric-teal/20 flex items-center justify-center text-electric-teal font-bold text-xs">
                      {t.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t.name}</p>
                      <p className="text-white/30 text-xs">{t.location}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {data.faqs?.length > 0 && (
        <section className="py-16 bg-primary-dark" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">Common Questions</span>
              <h2 className="text-3xl font-bold text-white mt-3">{data.service_title} FAQ</h2>
            </div>
            <div className="space-y-3">
              {data.faqs.map((faq, i) => (
                <div key={`fq-${faq.question.slice(0, 20)}-${i}`} className="bg-secondary-dark rounded-lg border border-white/5 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-4 flex items-center justify-between text-left group" aria-expanded={openFaq === i} data-testid={`faq-${i}`}>
                    <span className="text-white font-medium pr-4 group-hover:text-electric-teal transition-colors text-sm">{faq.question}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 text-electric-teal flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/40 flex-shrink-0" />}
                  </button>
                  {openFaq === i && <div className="px-6 pb-5"><p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-electric-teal" data-testid="bottom-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-dark tracking-tight mb-4">
            Ready for {data.service_title} in {location?.name}?
          </h2>
          <p className="text-primary-dark/70 text-lg mb-8">Free estimates. Same-day quotes available. Licensed and insured.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:7789602377" className="inline-flex items-center justify-center gap-2 bg-primary-dark text-white font-bold px-8 py-5 rounded-lg text-lg hover:bg-secondary-dark transition-all">
              <Phone className="w-5 h-5" /> Call 778.960.2377
            </a>
            <Link to="/book-estimate">
              <Button className="bg-white text-primary-dark font-bold px-8 py-5 text-lg hover:bg-white/90">
                <Zap className="w-5 h-5 mr-2" /> Book Online
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-primary-dark border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Island Coastal Electric. All rights reserved. | Tesla Certified | Generac Certified | BC Hydro Alliance Member | 778.960.2377
          </p>
          <p className="text-white/20 text-xs mt-2">
            Serving {location?.name} and all of {location?.region || 'Vancouver Island'}.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServiceLocationPage;
